/**
 * Widget UI string dictionary. Keys follow a `surface.purpose` convention
 * (e.g. `popup.askAnything`) so a new locale only needs to fill in the
 * same shape. The interface is the contract: any locale that omits a key
 * fails type-check at definition time, surfaces a default at runtime.
 *
 * Per docs/snowy/widget/2026-05-18-widget-rebuild-plan.md Phase 6.
 */
export interface Dictionary {
  // ───────── Popups + launcher ─────────
  "popup.dismiss": string;
  "popup.askAnything": string;
  "popup.callNow": string;
  "popup.preset.findAUnit": string;
  "popup.preset.sizeHelp": string;
  "popup.preset.ourPricing": string;
  "popup.preset.payBill": string;
  "popup.preset.manageAccount": string;
  "popup.preset.leaveAReview": string;
  "popup.dialogLabel.chatWithUs": string;
  "popup.dialogLabel.promotion": string;
  "popup.dialogLabel.inventory": string;
  "popup.inventory.reserveCta": string;
  "popup.inventory.continueLabel": string;
  "popup.inventory.monthlyRate": string;
  "popup.inventory.intent": string;

  // ───────── Bottom nav ─────────
  "tab.chat": string;
  "tab.call": string;
  "tab.account": string;

  // ───────── Chat shell ─────────
  "chat.emptyTitle": string;
  "chat.emptySubtitle": string;
  "chat.dateSeparator.today": string;
  "chat.dateSeparator.yesterday": string;
  "chat.input.placeholder": string;

  // ───────── Reservation funnel (Phase 3) ─────────
  "reservation.cancel": string;
  "reservation.back": string;
  "reservation.continue": string;
  "reservation.stepper.selectUnit": string;
  "reservation.stepper.selectPrice": string;
  "reservation.stepper.pay": string;
  "reservation.step1.filter": string;
  "reservation.step1.filterAdd": string;
  "reservation.step1.from": string;
  "reservation.step1.reserve": string;
  "reservation.step1.select": string;
  "reservation.step2.title": string;
  "reservation.step2.subtitle": string;
  "reservation.step2.heading": string;
  "reservation.step2.recommended": string;
  "reservation.step2.commitment.none": string;
  "reservation.step2.commitment.months": string; // {months}
  "reservation.step2.empty": string;
  "reservation.step2.confirm": string;
  "reservation.step2.popular": string;
  "reservation.tier.monthlyRate": string; // {rate}
  "reservation.step3.title": string;
  "reservation.step3.panelTitle": string;
  "reservation.step3.moveInSection": string;
  "reservation.step3.paymentSection": string;
  "reservation.step3.payment.card": string;
  "reservation.step3.payment.bankAccount": string;
  "reservation.step3.edit": string;
  "reservation.step3.insuranceSection": string;
  "reservation.step3.insuranceNone": string;
  "reservation.step3.contactSection": string;
  "reservation.step3.contact.fullName": string;
  "reservation.step3.contact.firstName": string;
  "reservation.step3.contact.lastName": string;
  "reservation.step3.contact.email": string;
  "reservation.step3.contact.phone": string;
  "reservation.step3.summary.title": string;
  "reservation.step3.summary.rent": string;
  "reservation.step3.summary.rentPromo": string; // {months}
  "reservation.step3.summary.moveInFee": string;
  "reservation.step3.summary.insurance": string; // {plan}
  "reservation.step3.summary.adminFee": string;
  "reservation.step3.summary.taxes": string;
  "reservation.step3.summary.total": string;
  "reservation.step3.terms": string;
  "reservation.step3.terms.prefix": string;
  "reservation.step3.terms.linkRental": string;
  "reservation.step3.autopay.label": string;
  "reservation.step3.autopay.terms": string;
  "reservation.step3.smsOptIn.label": string;
  "reservation.step3.smsOptIn.terms": string;
  "reservation.step3.pay": string;
  "reservation.step3.payCta": string; // {amount}
  "reservation.step3.processing": string;
  "reservation.step3.errorMissing": string;
  "reservation.step3.errorEmailFormat": string;
  "reservation.step3.payIntent": string; // {unitName}, {facilityName}, {tier}, {total}

  "reservation.step4.title": string;
  "reservation.step4.subtitle": string;
  "reservation.step4.confirmationLabel": string;
  "reservation.step4.unitLabel": string;
  "reservation.step4.close": string;

  // ───────── Transcript PDF ─────────
  "transcript.download": string; // aria-label / title on the ChatHeader button
  "transcript.fallbackBrand": string;
  "transcript.subtitle": string; // {generatedAt}
  "transcript.visitorLabel": string;
  "transcript.assistantLabel": string; // {brandName}
  "transcript.pageOf": string; // {current}, {total}
  "transcript.sessionId": string; // {id} (masked)
  "transcript.structured.locationPicker": string; // {count}
  "transcript.structured.unitPicker": string; // {count}, {locationName}
  "transcript.structured.buttons": string;
  "transcript.structured.profileForm": string;
  "transcript.structured.zipPrompt": string;
  "transcript.structured.unknown": string; // {kind}

  // ───────── Phone tab ─────────
  "phone.title.callUs": string;
  "phone.helpText": string;
  "phone.cta.callNow": string;
  "phone.disclaimer": string;
  "phone.emptyTitle": string;
  "phone.emptyMessage": string;

  // ───────── Account tab (sign-in) ─────────
  "account.signIn.title": string;
  "account.signIn.subtitle": string;
  "account.signIn.headline": string;
  "account.signIn.benefit1": string;
  "account.signIn.benefit2": string;
  "account.signIn.benefit3": string;
  "account.signIn.mobileLabel": string;
  "account.signIn.phonePlaceholder": string;
  "account.signIn.send": string;
  "account.signIn.sending": string;
  "account.signIn.disclaimer": string;
  "account.signIn.errorInvalidPhone": string;
  "account.signIn.errorSendFailed": string;
  "account.signIn.countrySearch": string;
  "account.signIn.countryNoMatches": string;
  "account.code.headerTitle": string;
  "account.code.title": string;
  "account.code.subtitle": string; // {phone} placeholder
  "account.code.verify": string;
  "account.code.verifying": string;
  "account.code.resendPrompt": string;
  "account.code.resend": string;
  "account.code.resending": string;
  "account.code.useDifferentNumber": string;
  "account.code.errorLength": string;
  "account.code.errorVerifyFailed": string;
  "account.code.errorWrong": string; // {triesLeft} placeholder
  "account.code.errorWrongNoCount": string;
  "account.code.errorExpired": string;
  "account.code.errorTooManyAttempts": string;
  "account.code.errorCodeNotFound": string;
  "account.code.errorSessionExpired": string;
  "account.code.errorInvalidInput": string;
  "account.code.errorGeneric": string;

  // ───────── Account tab (home) ─────────
  "account.home.title": string;
  "account.home.unavailable": string;
  "account.home.signOut": string;
  "account.home.balance": string;
  "account.home.unitPrefix": string;
  "account.home.due": string;
  "account.home.paidThrough": string;
  "account.home.accessCode": string;
  "account.home.daysPastDueSingular": string; // {days}
  "account.home.daysPastDuePlural": string; // {days}
  "account.home.empty.title": string;
  "account.home.empty.subtitle": string;
  "account.home.error": string;
  "account.home.loading": string;
  "account.home.myUnit": string;
  "account.home.gateAccessCode": string;
  "account.home.getGateCode": string;
  "account.home.gateCodeLoading": string;
  "account.home.gateCodeError": string;
  "account.home.gateCodeLocked": string;
  "account.home.nextPayment": string;
  "account.home.pastDue": string;
  "account.home.dueOn": string; // {date}
  "account.home.logOut": string;
  "account.home.errorTitle": string;
  "account.home.tryAgain": string;
  "account.home.payNow": string;
  /** Fallback when the failure cause is unknown (e.g. the request never completed). */
  "account.home.payError": string;
  /** Transient server-side failure — retrying is genuinely worth suggesting. */
  "account.home.payErrorUnavailable": string;
  /** Terminal: no payment integration, or the operator disabled online payment. */
  "account.home.payErrorNoIntegration": string;
  /** Heading for the card block shown when a balance has no online payment path. */
  "account.home.payByPhoneTitle": string;
  /** Body for that block when a number is configured — the number renders below it. */
  "account.home.payByPhoneHelp": string;
  /** Body for that block when the operator configured no number at all. */
  "account.home.payByPhoneNoNumber": string;
}

/**
 * English (US) is the canonical source — every other locale is checked
 * against this shape at compile time. Keys never collapse; missing keys
 * are an opt-in to fallback (the runtime gracefully degrades to en-US).
 */
export const EN_US: Dictionary = {
  "popup.dismiss": "Dismiss",
  "popup.askAnything": "Ask anything…",
  "popup.callNow": "Call Now",
  "popup.preset.findAUnit": "Find a Unit",
  "popup.preset.sizeHelp": "Size Help",
  "popup.preset.ourPricing": "Our Pricing",
  "popup.preset.payBill": "Pay Bill",
  "popup.preset.manageAccount": "Manage Account",
  "popup.preset.leaveAReview": "Leave a Review",
  "popup.dialogLabel.chatWithUs": "Chat with us",
  "popup.dialogLabel.promotion": "Promotion",
  "popup.dialogLabel.inventory": "Your unit",
  "popup.inventory.reserveCta": "Reserve now",
  "popup.inventory.continueLabel": "Continue where you left off",
  "popup.inventory.monthlyRate": "{rate}/mo",
  "popup.inventory.intent": "I'd like to reserve unit {unitName} at {facilityName}.",

  "tab.chat": "Chat",
  "tab.call": "Phone",
  "tab.account": "Account",

  "chat.emptyTitle": "Start a conversation",
  "chat.emptySubtitle": "Send a message to get started",
  "chat.dateSeparator.today": "Today",
  "chat.dateSeparator.yesterday": "Yesterday",
  "chat.input.placeholder": "Enter your message...",

  "reservation.cancel": "Cancel",
  "reservation.back": "Back",
  "reservation.continue": "Continue",
  "reservation.stepper.selectUnit": "Select Unit",
  "reservation.stepper.selectPrice": "Select Price",
  "reservation.stepper.pay": "Pay",
  "reservation.step1.filter": "Filter",
  "reservation.step1.filterAdd": "Add",
  "reservation.step1.from": "from",
  "reservation.step1.reserve": "Reserve",
  "reservation.step1.select": "Select",
  "reservation.step2.title": "Pick a commitment",
  "reservation.step2.subtitle": "Longer commitments save you more. Switch anytime before you check out.",
  "reservation.step2.heading": "Select a Price",
  "reservation.step2.recommended": "Recommended",
  "reservation.step2.commitment.none": "No commitment",
  "reservation.step2.commitment.months": "{months}-month commitment",
  "reservation.step2.empty": "No price options available right now. Hop over to Chat and we'll set this up.",
  "reservation.step2.confirm": "Confirm Selection",
  "reservation.step2.popular": "Popular",
  "reservation.tier.monthlyRate": "{rate}/mo",
  "reservation.step3.title": "Review + reserve",
  "reservation.step3.panelTitle": "💳 Review & Pay",
  "reservation.step3.moveInSection": "Move-in Date",
  "reservation.step3.paymentSection": "Payment Method",
  "reservation.step3.payment.card": "Card",
  "reservation.step3.payment.bankAccount": "US Bank Account",
  "reservation.step3.edit": "Edit",
  "reservation.step3.insuranceSection": "Insurance Coverage",
  "reservation.step3.insuranceNone": "No insurance",
  "reservation.step3.contactSection": "Contact",
  "reservation.step3.contact.fullName": "Full name",
  "reservation.step3.contact.firstName": "First name",
  "reservation.step3.contact.lastName": "Last name",
  "reservation.step3.contact.email": "Email",
  "reservation.step3.contact.phone": "Phone",
  "reservation.step3.summary.title": "Amount Due Today",
  "reservation.step3.summary.rent": "Rent",
  "reservation.step3.summary.rentPromo": "Rent (promo {months} months)",
  "reservation.step3.summary.moveInFee": "Move-in Fee",
  "reservation.step3.summary.insurance": "Insurance ({plan})",
  "reservation.step3.summary.adminFee": "Admin fee",
  "reservation.step3.summary.taxes": "Taxes & Fees",
  "reservation.step3.summary.total": "Total Due Today",
  "reservation.step3.terms": "I agree to the rental terms and conditions.",
  "reservation.step3.terms.prefix": "I agree to the",
  "reservation.step3.terms.linkRental": "Rental Agreement",
  "reservation.step3.autopay.label": "Enroll in autopay.",
  "reservation.step3.autopay.terms": "See terms",
  "reservation.step3.smsOptIn.label": "Opt in to SMS notifications for account purposes.",
  "reservation.step3.smsOptIn.terms": "See terms",
  "reservation.step3.pay": "Reserve & pay",
  "reservation.step3.payCta": "Pay {amount} & Reserve Unit",
  "reservation.step3.processing": "Sending you to chat…",
  "reservation.step3.errorMissing": "Please fill in name, email, and phone.",
  "reservation.step3.errorEmailFormat": "Email doesn't look right.",
  "reservation.step3.payIntent": "I'm ready to reserve unit {unitName} at {facilityName} on the {tier} tier ({total} due today).",
  "reservation.step4.title": "Reservation submitted",
  "reservation.step4.subtitle": "We've sent the details to your email and a Swivl team member will reach out to wrap up payment.",
  "reservation.step4.confirmationLabel": "Confirmation number",
  "reservation.step4.unitLabel": "Reserved unit",
  "reservation.step4.close": "Done",

  "transcript.download": "Download transcript",
  "transcript.fallbackBrand": "Chat transcript",
  "transcript.subtitle": "Generated {generatedAt}",
  "transcript.visitorLabel": "You",
  "transcript.assistantLabel": "{brandName}",
  "transcript.pageOf": "Page {current} of {total}",
  "transcript.sessionId": "Session {id}",
  "transcript.structured.locationPicker": "Location picker — {count} location(s) shown",
  "transcript.structured.unitPicker": "Unit picker — {count} unit(s) at {locationName}",
  "transcript.structured.buttons": "Quick buttons",
  "transcript.structured.profileForm": "Profile form requested",
  "transcript.structured.zipPrompt": "Zip-code prompt",
  "transcript.structured.unknown": "Structured message: {kind}",

  "phone.title.callUs": "Call us",
  "phone.helpText": "Need help? Call us now",
  "phone.cta.callNow": "Call Now",
  "phone.disclaimer": "Standard call rates may apply",
  "phone.emptyTitle": "No phone number on file",
  "phone.emptyMessage": "Ask your team to add a phone number under the widget's Tab Bar settings.",

  "account.signIn.title": "Sign in to your account",
  "account.signIn.subtitle": "We'll text a 6-digit code to verify your phone.",
  "account.signIn.headline": "Manage your unit anytime",
  "account.signIn.benefit1": "See all your units in one place",
  "account.signIn.benefit2": "Check your balance anytime",
  "account.signIn.benefit3": "Pay your bill in seconds",
  "account.signIn.mobileLabel": "Mobile Number",
  "account.signIn.phonePlaceholder": "(555) 123-4567",
  "account.signIn.send": "Send Code",
  "account.signIn.sending": "Sending code…",
  "account.signIn.disclaimer": "Message and data rates may apply",
  "account.signIn.errorInvalidPhone": "Enter a valid phone number.",
  "account.signIn.errorSendFailed": "Couldn't send a code. Try again in a minute.",
  "account.signIn.countrySearch": "Search countries",
  "account.signIn.countryNoMatches": "No matches",
  "account.code.headerTitle": "Verify Code",
  "account.code.title": "Enter your code",
  "account.code.subtitle": "To verify your number, use the one-time code sent via SMS to {phone}",
  "account.code.verify": "Verify & Log In",
  "account.code.verifying": "Verifying…",
  "account.code.resendPrompt": "Didn't receive the code?",
  "account.code.resend": "Resend",
  "account.code.resending": "Sending…",
  "account.code.useDifferentNumber": "Use a different number",
  "account.code.errorLength": "Codes are 6 digits.",
  "account.code.errorVerifyFailed": "Couldn't verify. Try again.",
  "account.code.errorWrong": "Wrong code. {triesLeft} {tryWord} left.",
  "account.code.errorWrongNoCount": "Wrong code.",
  "account.code.errorExpired": "That code expired. Send a new one.",
  "account.code.errorTooManyAttempts": "Too many wrong codes. Send a fresh one and try again.",
  "account.code.errorCodeNotFound": "Request a code first.",
  "account.code.errorSessionExpired": "Session expired — refresh the page.",
  "account.code.errorInvalidInput": "That phone number doesn't look right.",
  "account.code.errorGeneric": "Verification failed. Try again.",

  "account.home.title": "Account",
  "account.home.unavailable": "Account sign-in is unavailable right now. Try refreshing the page.",
  "account.home.signOut": "Sign out",
  "account.home.balance": "Balance",
  "account.home.unitPrefix": "Unit",
  "account.home.due": "Due:",
  "account.home.paidThrough": "Paid through:",
  "account.home.accessCode": "Access code:",
  "account.home.daysPastDueSingular": "{days} day past due",
  "account.home.daysPastDuePlural": "{days} days past due",
  "account.home.empty.title": "No units on file",
  "account.home.empty.subtitle": "We couldn't find any active units linked to your phone. Hop over to Chat if you think this is wrong.",
  "account.home.error": "Couldn't load your account. Try again in a minute.",
  "account.home.loading": "Loading…",
  "account.home.myUnit": "My Unit",
  "account.home.gateAccessCode": "Gate Access Code",
  "account.home.getGateCode": "Get Gate Access Code",
  "account.home.gateCodeLoading": "Getting your code…",
  "account.home.gateCodeError": "Couldn't get your code.",
  // Deliberately vague about when access returns: gate sync timing varies by
  // facility, so promising "instantly after you pay" would often be wrong.
  "account.home.gateCodeLocked": "Pay your balance to restore gate access.",
  "account.home.nextPayment": "Next Payment",
  "account.home.pastDue": "Past Due",
  "account.home.dueOn": "Due {date}",
  "account.home.logOut": "Log Out",
  "account.home.errorTitle": "Something went wrong",
  "account.home.tryAgain": "Try Again",
  "account.home.payNow": "Pay Now",
  "account.home.payError": "Couldn't start payment. Try again.",
  "account.home.payErrorUnavailable": "We couldn't start your payment. Please try again in a moment.",
  "account.home.payErrorNoIntegration": "Contact the facility to pay.",
  "account.home.payByPhoneTitle": "Pay by phone",
  "account.home.payByPhoneHelp": "Call the facility to pay your balance.",
  "account.home.payByPhoneNoNumber": "Contact the facility to pay your balance."
};
